import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { FunnelResponse } from '../../services/caseWorkflowService';
import {
    DWELL_CAPTION, EMPTY_CUSTOMER_MANAGED, MULTI_VERSION_CAPTION, count, hours, percent, transitionLabel
} from './caseWorkflowVocabulary';
import { MAGNITUDE_HUE } from './caseWorkflowPalette';
import { dsVar, ink } from '../../theme/tokens';

interface FunnelChartProps {
    funnel: FunnelResponse;
    onSelect?: (from: string, to: string) => void;
}

/**
 * How cases actually moved, step to step.
 *
 * An edge is "this case reached B having reached A". Edges that skip a stage
 * are real and are drawn rather than hidden: a P1 is auto-closed while its
 * siblings are still being scored, so ALERT_QUEUED -> REPORT_AUTO_CLOSED is a
 * legitimate bypass and not a missing prioritisation step.
 */
const FunnelChart: React.FC<FunnelChartProps> = ({ funnel, onSelect }) => {
    const edges = funnel.edges;
    const max = Math.max(...edges.map((e) => e.cases), 1);
    const partial = funnel.caseIdCoverage !== null && funnel.caseIdCoverage < 1;
    const spansAnEdit = (funnel.workflowVersions ?? []).length > 1;

    if (!edges.length) {
        return (
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>
                {funnel.caseIdCoverage === 0
                    ? EMPTY_CUSTOMER_MANAGED
                    : 'No case has moved between two steps in this window yet.'}
            </Typography>
        );
    }

    return (
        <Box>
            {/* Shown before the numbers, because it changes what they mean. */}
            {spansAnEdit && (
                <Typography sx={{ fontSize: '0.6875rem', color: ink('#FBBF24'), mb: 1.5 }}>
                    {MULTI_VERSION_CAPTION(funnel.workflowVersions)}
                </Typography>
            )}
            <Box className="flex flex-col gap-3">
                {edges.map((edge) => (
                    <Box
                        key={`${edge.from}->${edge.to}`}
                        onClick={onSelect ? () => onSelect(edge.from, edge.to) : undefined}
                        className={onSelect ? 'cursor-pointer' : ''}
                    >
                        <Box className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-1">
                            <Box className="flex items-center gap-1.5 min-w-0">
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }} noWrap>
                                    {transitionLabel(edge.from)}
                                </Typography>
                                <ArrowForwardIcon sx={{ fontSize: 13, color: dsVar('fg-faint'), flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-soft'), fontWeight: 600 }} noWrap>
                                    {transitionLabel(edge.to)}
                                </Typography>
                            </Box>
                            <Box className="flex items-baseline gap-2 flex-shrink-0">
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), fontVariantNumeric: 'tabular-nums' }}>
                                    {count(edge.cases)} cases
                                </Typography>
                                <Tooltip title={DWELL_CAPTION} arrow placement="top">
                                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), cursor: 'help', fontVariantNumeric: 'tabular-nums' }}>
                                        {hours(edge.p50Hours)} median · {hours(edge.p90Hours)} p90
                                    </Typography>
                                </Tooltip>
                            </Box>
                        </Box>
                        <Box sx={{ height: 8, borderRadius: '4px', backgroundColor: dsVar('page'), overflow: 'hidden' }}>
                            <Box
                                sx={{
                                    height: '100%',
                                    width: `${Math.max((edge.cases / max) * 100, 2)}%`,
                                    backgroundColor: MAGNITUDE_HUE,
                                    borderRadius: '4px'
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Box>

            <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 2 }}>
                {DWELL_CAPTION}
                {' '}
                {/*
                  * On the chart itself, not in a tooltip: a business reader reads
                  * an unexplained gap between stage counts as data loss.
                  */}
                {partial
                    ? `Only ${percent(funnel.caseIdCoverage)} of transitions carry a case id, so these stage counts are partial.`
                    : 'Every transition in this window carries a case id.'}
            </Typography>
        </Box>
    );
};

export default FunnelChart;
